import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchFromStrapi } from "../api/strapi";
import LazyImage from "./LazyImage";

/* ---------------- Skeletons ---------------- */

const Skeleton = ({ style }) => (
  <div
    className="skeleton"
    style={{
      background:
        "linear-gradient(110deg, #f0f4fa 25%, #e6edf7 37%, #f0f4fa 63%)",
      backgroundSize: "400% 100%",
      animation: "skeleton 1.4s ease infinite",
      borderRadius: "8px",
      ...style,
    }}
  />
);

const FeaturedSkeleton = () => (
  <Skeleton style={{ height: 420 }} />
);

const SideSkeleton = () => (
  <div
    style={{
      display: "flex",
      gap: 16,
      padding: 16,
      borderRadius: 8,
      background: "#f5f9ff",
    }}
  >
    <Skeleton style={{ width: 120, height: 88 }} />
    <div style={{ flex: 1 }}>
      <Skeleton style={{ height: 12, width: "40%", marginBottom: 10 }} />
      <Skeleton style={{ height: 16, width: "100%" }} />
    </div>
  </div>
);

const GridSkeleton = () => (
  <div style={{ background: "#fff", borderRadius: 8, overflow: "hidden" }}>
    <Skeleton style={{ height: 180 }} />
    <div style={{ padding: 16 }}>
      <Skeleton style={{ height: 12, width: "35%", marginBottom: 10 }} />
      <Skeleton style={{ height: 16, width: "90%" }} />
    </div>
  </div>
);

/*--------------- Featured card --------------- */ 


/* ---------------- Component ---------------- */

export default function LatestInsights() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [featuredIndex, setFeaturedIndex] = useState(0);

  useEffect(() => {
    if (!blogs.length) return;

    const interval = setInterval(() => {
      setFeaturedIndex((prev) => (prev + 1) % Math.min(4, blogs.length));
    }, 4000); // 5 seconds feels right for reading

    return () => clearInterval(interval);
  }, [blogs]);

  


  useEffect(() => {
    async function fetchData() {
      try {
        const res = await fetchFromStrapi("/ecls");
        const sorted = [...(res?.data || [])].sort((a, b) => {
          const dateA = new Date(a?.attributes?.date || 0);
          const dateB = new Date(b?.attributes?.date || 0);
          return dateB - dateA;
        });
        setBlogs(sorted);
      } catch (err) {
        console.error("Error fetching blogs:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const getImageUrl = item =>
    item?.attributes?.image?.data?.attributes?.url;

  /* ---------------- Loading state ---------------- */

  if (loading) {
    return (
      <section style={{ padding: "80px 0" }}>
        <div className="container">
          <h2
            style={{
              fontSize: 32,
              fontWeight: 700,
              color: "#0b1f44",
              marginBottom: 32,
            }}
          >
            Discover
          </h2>

          <div className="blog-featured-grid">
            <FeaturedSkeleton />
            <div style={{ display: "grid", gap: 24 }}>
              <SideSkeleton />
              <SideSkeleton />
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              gap: 24,
            }}
          >
            <GridSkeleton />
            <GridSkeleton />
            <GridSkeleton />
          </div>
        </div>
      </section>
    );
  }

  /* ---------------- Data slicing ---------------- */

  const featured = blogs[featuredIndex];  // cycles through the 4 most recent
  const side = blogs[4];                  // 5th most recent
  const grid = blogs.slice(5, 9);         // next 4
  

  return (
    <section >
      <div className="container">
        <p
          className="text-uppercase fw-semibold mb-1"
          style={{ fontSize: "0.75rem", letterSpacing: "0.1em", color: "#009EDC" }}
        >
          News and insights on all things tech
        </p>
        <h2
          style={{
            fontSize: 32,
            fontWeight: 700,
            color: "#0b1f44",
            marginBottom: 32,
          }}
        >
          Insights
        </h2>

        {/* TOP ROW */}
        <div className="blog-featured-grid">
          {/* Featured */}
          {featured && (
            <Link
              to={`/blog/${featured.id}`}
              style={{
                display: "block",
                borderRadius: 8,
                overflow: "hidden",
                background: "#f5f9ff",
                textDecoration: "none",
                color: "#0b1f44",
                height: "100%",
                position: "relative",
              }}
            >
              {/* Image */}
              <div style={{ height: "100%" }}>
                <LazyImage
                  src={getImageUrl(featured)}
                  alt={featured.attributes.title}
                  objectFit="cover"
                  objectPosition="center 20%"
                />
              </div>

              {/* Overlay content */}
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(to top, rgba(11,31,68,0.75), rgba(11,31,68,0.1))",
                  padding: 28,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-end",
                }}
              >
                <small style={{ color: "#dbe7ff", marginBottom: 6 }}>
                  {featured.attributes.date}
                </small>

                <h3
                  style={{
                    color: "#fff",
                    fontWeight: 700,
                    lineHeight: 1.25,
                    maxWidth: "90%",
                  }}
                >
                  {featured.attributes.title}
                </h3>
              </div>
            </Link>
          )}


          {/* Side cards */}
         {side && (
           <Link
              to={`/blog/${side.id}`}
              style={{
                background: "#fff",
                borderRadius: 8,
                overflow: "hidden",
                textDecoration: "none",
                color: "#0b1f44",
              }}
            >
              <LazyImage
                src={getImageUrl(side)}
                alt={side.attributes.title}
                objectFit="cover"
                objectPosition="top center"
                height={280}
              />
              <div style={{ padding: 16 }}>
                <small style={{ color: "#6b7a99" }}>
                  {side.attributes.date}
                </small>
                <h6 style={{ fontWeight: 600 }}>
                  {side.attributes.title}
                </h6>
              </div>
            </Link>
          )}

        </div>

        {/* LOWER GRID */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: 24,
          }}
        >
          {grid.map(item => (
            <Link
              key={item.id}
              to={`/blog/${item.id}`}
              style={{
                background: "#fff",
                borderRadius: 8,
                overflow: "hidden",
                textDecoration: "none",
                color: "#0b1f44",
              }}
            >
              <LazyImage
                src={getImageUrl(item)}
                alt={item.attributes.title}
                objectFit="cover"
                objectPosition="top center"
                height={280}
              />
              <div style={{ padding: 16 }}>
                <small style={{ color: "#6b7a99" }}>
                  {item.attributes.date}
                </small>
                <h6 style={{ fontWeight: 600 }}>
                  {item.attributes.title}
                </h6>
              </div>
            </Link>
          ))}
        </div>

        <div style={{ textAlign: "right", marginTop: 32 }}>
          <Link
            to="/blog"
            style={{ fontWeight: 600, textDecoration: "underline" }}
          >
            View all →
          </Link>
        </div>
      </div>
    </section>
  );
}
